import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { dashboardApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Bell, AlertTriangle, Clock, CheckCircle2, Loader2 } from 'lucide-react';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await dashboardApi.getAlerts();
        setAlerts(response.data);
      } catch (error) {
        toast.error('Failed to fetch alerts');
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const getPriorityStyle = (priority) => {
    const styles = {
      high: 'border-l-4 border-l-red-500 bg-red-50',
      medium: 'border-l-4 border-l-yellow-500 bg-yellow-50',
      low: 'border-l-4 border-l-blue-500 bg-blue-50',
    };
    return styles[priority] || styles.low;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return <Badge className={`${variants[priority]} capitalize`}>{priority}</Badge>;
  };

  const highPriorityCount = alerts.filter(a => a.priority === 'high').length;
  const mediumPriorityCount = alerts.filter(a => a.priority === 'medium').length;
  const lowPriorityCount = alerts.filter(a => a.priority === 'low').length;

  return (
    <Layout
      title="Alerts"
      subtitle="Compliance & Renewal Alerts"
    >
      <div className="space-y-6" data-testid="alerts-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Alerts</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">{alerts.length}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">High Priority</p>
                  <p className="text-2xl font-bold text-red-600 tabular-nums">{highPriorityCount}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Medium Priority</p>
                  <p className="text-2xl font-bold text-yellow-600 tabular-nums">{mediumPriorityCount}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Low Priority</p>
                  <p className="text-2xl font-bold text-blue-600 tabular-nums">{lowPriorityCount}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts List */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              All Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-900">All Clear!</p>
                <p className="text-slate-500">No pending alerts. All documents are up to date.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <div
                    key={alert.id || index}
                    className={`p-4 rounded-lg ${getPriorityStyle(alert.priority)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                          alert.priority === 'high' ? 'text-red-500' :
                          alert.priority === 'medium' ? 'text-yellow-500' :
                          'text-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium text-slate-900">{alert.entity_name}</p>
                          <p className="text-sm text-slate-600 mt-0.5">{alert.message}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-sm text-slate-500">
                              Type: <span className="capitalize">{alert.alert_type.replace('_', ' ')}</span>
                            </span>
                            <span className="text-sm text-slate-500">
                              Expires: {alert.expiry_date}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-white">
                          {alert.days_remaining} days left
                        </Badge>
                        {getPriorityBadge(alert.priority)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Alerts;
